import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ASSETS } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/listings", label: "Featured Listings" },
  { to: "/office-listings", label: "Office Listings" },
  { to: "/about", label: "About Mike" },
  { to: "/process", label: "The Process" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = pathname === "/";
  // Transparent over hero only when at top of home page
  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        solid ? "bg-paper/95 backdrop-blur border-b border-navy/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={ASSETS.remaxLogo} alt="RE/MAX Performance Realty" className="h-9 w-auto" />
          <span className={`hidden sm:block h-8 w-px ${solid ? "bg-navy/20" : "bg-white/40"}`} />
          <span className={`hidden sm:flex flex-col leading-none ${solid ? "text-navy" : "text-white"}`}>
            <span className="font-heading text-lg font-semibold">Mike Sidhu</span>
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em] opacity-70 mt-0.5">Personal Real Estate Corporation</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-body text-sm tracking-wide transition-colors ${
                pathname === l.to
                  ? "text-brass"
                  : solid ? "text-navy hover:text-brass" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/book"
            data-testid="nav-cta-contact"
            className="bg-navy text-white text-sm px-5 py-2.5 hover:bg-brass transition-colors duration-300"
          >
            Book a Consultation
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className={`lg:hidden ${solid ? "text-navy" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-paper border-t border-navy/10 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-body text-base ${pathname === l.to ? "text-brass" : "text-navy"}`}
            >
              {l.label}
            </Link>
          ))}
          <Link to="/book" className="bg-navy text-white text-center py-3 mt-2">Book a Consultation</Link>
        </div>
      )}
    </header>
  );
};
